import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import './ConnectivityTest.css';

function ConnectivityTest({ sourceConnection, targetConnection, onTestComplete, autoRun = false }) {
    const [testing, setTesting] = useState(false);
    const [testResults, setTestResults] = useState(null);
    const [currentStep, setCurrentStep] = useState(null);

    const runConnectivityTest = useCallback(async () => {
        setTesting(true);
        setTestResults(null);
        setCurrentStep('source');

        try {
            // Step 1: Test Source Connection
            console.log('[Connectivity Test] Testing source connection...');
            const sourceResponse = await fetch(`${API_BASE_URL}/api/dms/test-source`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ connectionId: sourceConnection.id })
            });
            const sourceResult = await sourceResponse.json();

            // Step 2: Test Target Connection
            setCurrentStep('target');
            console.log('[Connectivity Test] Testing target connection...');
            const targetResponse = await fetch(`${API_BASE_URL}/api/dms/test-target`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ connectionId: targetConnection.id })
            });
            const targetResult = await targetResponse.json();

            // Step 3: Test Bridge
            setCurrentStep('bridge');
            console.log('[Connectivity Test] Testing bridge connectivity...');
            const bridgeResponse = await fetch(`${API_BASE_URL}/api/dms/test-bridge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sourceId: sourceConnection.id,
                    targetId: targetConnection.id
                })
            });
            const bridgeResult = await bridgeResponse.json();

            setCurrentStep('complete');
            setTestResults({
                source: sourceResult,
                target: targetResult,
                bridge: bridgeResult,
                overallSuccess: sourceResult.success && targetResult.success && bridgeResult.success
            });

            if (onTestComplete) {
                onTestComplete({
                    success: sourceResult.success && targetResult.success && bridgeResult.success,
                    results: { source: sourceResult, target: targetResult, bridge: bridgeResult }
                });
            }

        } catch (error) {
            console.error('[Connectivity Test] Failed:', error);
            setTestResults({
                error: error.message,
                overallSuccess: false
            });
            setCurrentStep('error');
        } finally {
            setTesting(false);
        }
    }, [sourceConnection, targetConnection, onTestComplete]);

    // Auto-run test when component mounts if autoRun is true
    useEffect(() => {
        if (autoRun && sourceConnection && targetConnection && !testing && !testResults) {
            runConnectivityTest();
        }
    }, [autoRun, sourceConnection, targetConnection, testing, testResults, runConnectivityTest]);

    const getStatusIcon = (success) => {
        if (success === null || success === undefined) return '⏳';
        return success ? '✅' : '❌';
    };

    const getLatencyColor = (latencyMs) => {
        if (!latencyMs) return '#64748b';
        if (latencyMs < 100) return '#10b981';
        if (latencyMs < 200) return '#f59e0b';
        return '#ef4444';
    };

    const renderConnectionDetails = (result, label) => {
        if (!result) return null;

        return (
            <div className="connection-test-details">
                <div className="test-header">
                    <h4>{getStatusIcon(result.success)} {label}</h4>
                    {result.connectionName && <span className="connection-name">{result.connectionName}</span>}
                </div>

                {result.success ? (
                    <div className="test-success-details">
                        <div className="detail-grid">
                            <div className="detail-item">
                                <span className="detail-label">🌐 Port Reachable:</span>
                                <span className="detail-value">{result.portReachable ? '✅ Yes' : '❌ No'}</span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">🔐 Authenticated:</span>
                                <span className="detail-value">{result.authenticated ? '✅ Yes' : '❌ No'}</span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">⚡ Latency:</span>
                                <span
                                    className="detail-value latency-value"
                                    style={{ color: getLatencyColor(result.latencyMs) }}
                                >
                                    {result.latencyMs}ms
                                </span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">🔒 SSL/TLS:</span>
                                <span className="detail-value">{result.sslEnabled ? '✅ Enabled' : '⚠️ Disabled'}</span>
                            </div>
                            {result.databaseVersion && (
                                <div className="detail-item full-width">
                                    <span className="detail-label">📦 Version:</span>
                                    <span className="detail-value version-text">{result.databaseVersion}</span>
                                </div>
                            )}
                        </div>

                        {result.capabilities && (
                            <div className="capabilities-section">
                                <h5>🔧 Capabilities</h5>
                                <div className="capability-badges">
                                    {result.capabilities.supportsCDC && (
                                        <span className="capability-badge cdc-supported">
                                            ✨ CDC Supported
                                        </span>
                                    )}
                                    {result.capabilities.cdcReady && (
                                        <span className="capability-badge cdc-ready">
                                            🚀 CDC Ready
                                        </span>
                                    )}
                                    {result.capabilities.canWrite && (
                                        <span className="capability-badge can-write">
                                            ✍️ Write Access
                                        </span>
                                    )}
                                    {result.capabilities.replicationSlots && (
                                        <span className="capability-badge">
                                            📡 Replication Slots
                                        </span>
                                    )}
                                    {result.capabilities.binlogEnabled && (
                                        <span className="capability-badge">
                                            📝 Binlog Enabled
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {result.warnings && result.warnings.length > 0 && (
                            <div className="warnings-section">
                                <h5>⚠️ Warnings</h5>
                                {result.warnings.map((warning, idx) => (
                                    <div key={idx} className="warning-item">
                                        {warning}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="test-error-details">
                        {result.errors && result.errors.length > 0 && (
                            <div className="errors-section">
                                <h5>❌ Errors</h5>
                                {result.errors.map((error, idx) => (
                                    <div key={idx} className="error-item">
                                        {error}
                                    </div>
                                ))}
                            </div>
                        )}

                        {result.suggestions && result.suggestions.length > 0 && (
                            <div className="suggestions-section">
                                <h5>💡 Suggestions</h5>
                                {result.suggestions.map((suggestion, idx) => (
                                    <div key={idx} className="suggestion-item">
                                        {suggestion}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="connectivity-test-container">
            <div className="connectivity-test-header">
                <h3>🔍 Connectivity & Bridge Test</h3>
                <p>Validate network paths and database connectivity before migration</p>
            </div>

            {/* Visual Flow Diagram */}
            <div className="connectivity-flow">
                <div className={`flow-node source ${currentStep === 'source' ? 'active' : ''} ${testResults?.source?.success ? 'success' : testResults?.source ? 'error' : ''}`}>
                    <div className="node-icon">📤</div>
                    <div className="node-label">Source</div>
                    <div className="node-name">{sourceConnection?.name}</div>
                    {testResults?.source && (
                        <div className="node-status">{getStatusIcon(testResults.source.success)}</div>
                    )}
                </div>

                <div className="flow-arrow">
                    <div className="arrow-line"></div>
                    <div className="arrow-head">→</div>
                </div>

                <div className={`flow-node runner ${currentStep === 'bridge' ? 'active' : ''}`}>
                    <div className="node-icon">🖥️</div>
                    <div className="node-label">DMS Runner</div>
                    <div className="node-name">Migration Server</div>
                </div>

                <div className="flow-arrow">
                    <div className="arrow-line"></div>
                    <div className="arrow-head">→</div>
                </div>

                <div className={`flow-node target ${currentStep === 'target' ? 'active' : ''} ${testResults?.target?.success ? 'success' : testResults?.target ? 'error' : ''}`}>
                    <div className="node-icon">📥</div>
                    <div className="node-label">Target</div>
                    <div className="node-name">{targetConnection?.name}</div>
                    {testResults?.target && (
                        <div className="node-status">{getStatusIcon(testResults.target.success)}</div>
                    )}
                </div>
            </div>

            {/* Test Button */}
            <div className="test-action">
                <button
                    className="btn-test-connectivity"
                    onClick={runConnectivityTest}
                    disabled={testing || !sourceConnection || !targetConnection}
                >
                    {testing ? '🔄 Testing...' : '🧪 Run Connectivity Test'}
                </button>
            </div>

            {/* Test Results */}
            {testResults && (
                <div className="test-results-container">
                    {testResults.overallSuccess ? (
                        <div className="overall-success">
                            <div className="success-icon">✅</div>
                            <h3>Connectivity Test Passed!</h3>
                            <p>Source and target databases are reachable and ready for migration</p>
                        </div>
                    ) : testResults.error ? (
                        <div className="overall-error">
                            <div className="error-icon">❌</div>
                            <h3>Connectivity Test Failed</h3>
                            <p>{testResults.error}</p>
                        </div>
                    ) : (
                        <div className="overall-warning">
                            <div className="warning-icon">⚠️</div>
                            <h3>Connectivity Issues Detected</h3>
                            <p>Please review the details below and fix the issues before proceeding</p>
                        </div>
                    )}

                    {/* Detailed Results */}
                    <div className="detailed-results">
                        {testResults.source && renderConnectionDetails(testResults.source, 'Source Connection')}
                        {testResults.target && renderConnectionDetails(testResults.target, 'Target Connection')}

                        {/* Bridge Recommendations */}
                        {testResults.bridge && testResults.bridge.recommendations && testResults.bridge.recommendations.length > 0 && (
                            <div className="bridge-recommendations">
                                <h4>🎯 Migration Recommendations</h4>
                                {testResults.bridge.recommendations.map((rec, idx) => (
                                    <div key={idx} className="recommendation-item">
                                        {rec}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ConnectivityTest;
